import GeoStats from 'geostats';
import { LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import { Feature } from 'geojson';

const recursion = <T = unknown, U = unknown>(index: number, inputArray: T[], outputArray: U[]): void => {
  if (index >= inputArray.length) return;

  if (Array.isArray(inputArray[index]) && Array.isArray((inputArray[index] as any[])?.[0])) {
    recursion(0, inputArray[index] as [], outputArray);
  } else {
    outputArray.push(inputArray[index] as unknown as U);
  }

  recursion(index + 1, inputArray, outputArray);
};

export const flattenCoordinates = <T, U = unknown>(inputArray: T[]): U[] => {
  const outputArray: U[] = [];
  recursion<T, U>(0, inputArray, outputArray);
  return outputArray;
};

export const getCenterByCoordinates = (coordinates: any[]): LatLngExpression => {
  coordinates = flattenCoordinates<any, any>(coordinates);
  let minLon: number | undefined;
  let minLat: number | undefined;
  let maxLon: number | undefined;
  let maxLat: number | undefined;

  const lonValues = coordinates.map((coor) => coor[0]);
  const latValues = coordinates.map((coor) => coor[1]);

  lonValues.forEach((v) => {
    if (minLon === undefined || v < minLon) {
      minLon = v;
    }

    if (maxLon === undefined || v > maxLon) {
      maxLon = v;
    }
  });

  latValues.forEach((v) => {
    if (minLat === undefined || v < minLat) {
      minLat = v;
    }

    if (maxLat === undefined || v > maxLat) {
      maxLat = v;
    }
  });

  if (maxLon === undefined || minLon === undefined || maxLat === undefined || minLat === undefined) {
    throw new Error('Some boundaries are not defined.');
  }

  const xMid = (maxLon - minLon) / 2 + minLon;
  const yMid = (maxLat - minLat) / 2 + minLat;

  return [yMid, xMid];
};

export const buildBbox = (selectedFeatures: Feature[]): LatLngBoundsExpression => {
  let minLon: number | undefined;
  let minLat: number | undefined;
  let maxLon: number | undefined;
  let maxLat: number | undefined;

  selectedFeatures?.forEach((feature: Feature) => {
    if (feature?.geometry && 'coordinates' in feature?.geometry) {
      const coordinates: LatLngExpression[] = flattenCoordinates(feature.geometry.coordinates as any[]);
      const lonValues = coordinates.map((coor) => (coor as any)[0]);
      const latValues = coordinates.map((coor) => (coor as any)[1]);

      lonValues.forEach((v) => {
        if (minLon === undefined || v < minLon) {
          minLon = v;
        }

        if (maxLon === undefined || v > maxLon) {
          maxLon = v;
        }
      });

      latValues.forEach((v) => {
        if (minLat === undefined || v < minLat) {
          minLat = v;
        }

        if (maxLat === undefined || v > maxLat) {
          maxLat = v;
        }
      });
    }
  });

  return [
    [minLat ?? 0, minLon ?? 0],
    [maxLat ?? 0, maxLon ?? 0],
  ];
};

export const getEqIntervals = (serie: number[], nbClasses: number, min: number, max: number): number[] => {
  const g = new GeoStats();
  g.setSerie(serie);
  return g.getClassEqInterval(nbClasses, min, max);
};

export const getStdDeviation = (serie: number[], nbClasses: number): number[] => {
  const g = new GeoStats();
  g.setSerie(serie);
  return g.getClassStdDeviation(nbClasses, true);
};
